import * as React from 'react';
import { authManager } from 'singletons';

import AccountPicker from 'components/accountPicker';
import AddEditUserModal from 'components/addEditUserModal';
import AddUsersFromFileModal from 'components/addUsersFromFileModal';
import DeleteUserModal from 'components/deleteUserModal';
import StatusDisplay from 'components/statusDisplay';
import { UserTable } from 'components/userTable';
import { Spinner } from 'components/common/spinner';
import TabSelector from 'components/common/tabSelector';
import SearchInput from '@ps/payscale-shared-ui/lib/SearchInput';

import { selectAccount, selectProduct, filterUsers } from 'actions/data';
import {
    resetUserPassword,
    inviteUser,
    inviteUsers,
    editUser,
    deleteUser,
    deleteInvite,
    unlockUserAccount } from 'actions/user';
import { clearStatus } from 'actions/status';

import { Status, StatusType } from 'types/status';
import UserDetails from 'types/userDetails';
import { killEvent } from 'utils/domUtils';
import strings from 'utils/strings';

import logo from 'images/payscale_logo_white.svg';
import './app.scss';
import { getRoleNames, getName } from 'utils/user';

const logout = async () => {
    await authManager.logout();
};

export interface AppProps {
    accounts: MasterAccountDictionary;      // list of available accounts for this product
    currentAccountId: string;               // current selected account
    currentAccountDetails?: MasterAccount;  // current selected account
    currentProduct: string;                 // current selected product
    dispatch?: Function;
    productList: string[];                  // list of available products for this account
    roles: UserRole[];                      // list of all possible user roles
    status: Status | null;                  // current success/error state, if present
    users: User[] | null;                   // list of all possible users
    visibleUsers: User[] | null;            // list of visible users, differnet only if search in effect
}

export interface AppState {
    currentUser: User | null;             // current selected/edited user
    showAddEditDialog: boolean;           // whether or not add/edit user dialog is visible
    showConfirmDeleteDialog: boolean;     // whether or not delete confirmation dialog is visible
    showBulkAddDialog: boolean;           // whether or not bulk user add dialog is visible
}

class App extends React.PureComponent<AppProps, AppState> {
    private _searchInput: SearchInput | null;

    constructor(props: AppProps) {
        super(props);
        this.state = Object.assign(
            {},
            this.state,
            {
                currentUser: null,
                showAddEditDialog: false,
                showBulkAddDialog: false,
                showConfirmDeleteDialog: false
            });

        // bind any methods used as event callbacks/passed to sub-components
        this._onProductSelect = this._onProductSelect.bind(this);
        this._addUser = this._addUser.bind(this);
        this._editUser = this._editUser.bind(this);
        this._deleteUser = this._deleteUser.bind(this);
        this._bulkAddUser = this._bulkAddUser.bind(this);
        this._filterUsers = this._filterUsers.bind(this);
        this._selectAccount = this._selectAccount.bind(this);
        this._downloadUsers = this._downloadUsers.bind(this);
    }

    render(): React.ReactNode {
        const {
                accounts,
                currentAccountId,
                currentProduct,
                users,
                currentAccountDetails } = this.props;

        return (
            <div className="app">
                {this._renderHeader()}
                <div className="app__content">
                    {this._renderTitle()}
                    {this._renderProductTabs()}
                    {this._renderStatus()}
                    <div className="indent-container account-actions-container">
                        <AccountPicker
                            currentAccountId={currentAccountId}
                            accounts={accounts}
                            onAccountChange={this._selectAccount}
                            userCount={users ? users.length : null}
                            currentAccountDetails={currentAccountDetails}
                        />
                        <div className="user-actions-container">
                            <div className="search-container">
                                <SearchInput
                                    placeholderText={strings.searchUsers}
                                    onChange={this._filterUsers}
                                    ref={ref => this._searchInput = ref}
                                />
                            </div>
                            <div className="add-user-container">
                                <button
                                    className="add-user-button"
                                    disabled={!currentProduct}
                                    onClick={this._addUser}
                                >
                                    <i className="icon-plus" />
                                    {strings.addUser}
                                </button>
                                <a className="download-link" href="#" onClick={this._downloadUsers}>
                                    {strings.downloadUsers}
                                </a>
                                <a className="bulk-add-link" href="#" onClick={this._bulkAddUser}>
                                    {strings.addUsersFromFile}
                                </a>
                            </div>
                        </div>
                    </div>
                    {this._renderUserTable()}
                    {this._renderAddEditUserModal()}
                    {this._renderDeleteUserModal()}
                    {this._renderAddUsersFromFileModal()}
                </div>
            </div>
        );
    }

    // Render page header
    private _renderHeader(): React.ReactNode {
        return (
            <div className="header app__header">
                <div className="header__logo-container">
                    <img src={logo} className="header__logo-img" />
                </div>
                <button
                    className="header__logout-button"
                    onClick={logout}
                >
                    {strings.logoutButton}
                </button>
            </div>
        );
    }

    private _renderTitle(): React.ReactNode {
        return (
            <div className="indent-container">
                <h1 className="app__title">{strings.userManagement}</h1>
            </div>
        );
    }

    private _renderProductTabs(): React.ReactNode {
        const { currentProduct, productList } = this.props;
        return (
            <div className="indent-container tab-container">
                <TabSelector
                    items={productList}
                    currentItem={currentProduct}
                    onItemSelect={this._onProductSelect}
                />
                <div className="tab-container-border" />
            </div>
        );
    }

    private _renderStatus(): React.ReactNode {
        const { dispatch, status } = this.props;
        const onClearStatus = (): void => dispatch && dispatch(clearStatus());
        return (
            <div className="indent-container">
                <StatusDisplay
                    type={status ? status.type : StatusType.None}
                    title={status ? status.title : ''}
                    message={status ? status.message : ''}
                    onClose={onClearStatus}
                />
            </div>
        );
    }

    private _renderUserTable(): React.ReactNode {
        const { dispatch, visibleUsers, currentProduct, currentAccountId } = this.props;
        const unlockUserCallback = (user: User) => dispatch && dispatch(unlockUserAccount(user));
        const userActions = [
            {
                text: strings.edit,
                callback: (user: User) => this._editUser(user)
            },
            {
                text: strings.resetPassword,
                callback: (user: User) => dispatch && dispatch(resetUserPassword(user, currentProduct))
            },
            {
                icon: 'icon-trash',
                tooltip: strings.deleteUser,
                callback: (user: User) => this._deleteUser(user)
            }
        ];

        const inviteActions = [
            {
                icon: 'icon-trash',
                tooltip: strings.deleteInvite,
                callback: (user: User) => dispatch && dispatch(deleteInvite(currentAccountId, user))
            }
        ];

        return (
            <div className="indent-container user-table-container">
                <div className="user-table-wrapper">
                    <UserTable
                        users={visibleUsers}
                        userActions={userActions}
                        inviteActions={inviteActions}
                        unlockUserCallback={unlockUserCallback}
                    />
                    {!visibleUsers ? <Spinner className="user-table__spinner"/> : null}
                </div>
            </div>
        );
    }

    private _renderDeleteUserModal(): React.ReactNode {
        const { currentAccountId, dispatch } = this.props;
        const { showConfirmDeleteDialog, currentUser } = this.state;
        const hideCallback = () => this.setState({ showConfirmDeleteDialog: false });
        const commitCallback = () => {
            dispatch?.(deleteUser(currentAccountId, currentUser));
            this._clearSearch();
        };

        return (
            <DeleteUserModal
                show={showConfirmDeleteDialog}
                onHide={hideCallback}
                onCommit={commitCallback}
                userName={currentUser ? currentUser.userName : ''}
            />
        );
    }

    private _renderAddEditUserModal(): React.ReactNode {
        const { currentAccountId, currentProduct, dispatch, roles, currentAccountDetails } = this.props;
        const { showAddEditDialog, currentUser } = this.state;
        const currentUserDetails: UserDetails | null = currentUser ? {
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            email: currentUser.userName,
            roleIds: currentUser.roles.map(r => r.id)
        } : null;

        const addUserCallback = (details: UserDetails, sendInvite: boolean): void => {
            dispatch?.(inviteUser(currentAccountId, currentProduct, details, currentAccountDetails, sendInvite));
            this._clearSearch();
        };

        const editUserCallback = (details: UserDetails): void => {
            if (!currentUser) {
                return;
            }
            dispatch?.(editUser(currentAccountId, currentUser.id, details));
            this._clearSearch();
        };

        const hideCallback = () => {
            this.setState({ showAddEditDialog: false });
        };

        return (
            <AddEditUserModal
                onCommit={
                    currentUserDetails ?
                        editUserCallback :
                        addUserCallback
                }
                onHide={hideCallback}
                roles={roles}
                show={showAddEditDialog}
                userDetails={currentUserDetails}
                currentAccountDetails={currentAccountDetails}
            />
        );
    }

    private _renderAddUsersFromFileModal(): React.ReactNode {
        const { currentAccountId, currentProduct, dispatch, currentAccountDetails } = this.props;
        const { showBulkAddDialog } = this.state;

        const hideCallback = () => {
            this.setState({ showBulkAddDialog: false });
        };

        const addUsers = (users: UserDetails[], sendInvite: boolean): void => {
            dispatch?.(inviteUsers(currentAccountId, currentProduct, users, currentAccountDetails, sendInvite));
            this._clearSearch();
        };

        return (
            <AddUsersFromFileModal
                onCommit={addUsers}
                onHide={hideCallback}
                show={showBulkAddDialog}
                showInviteDropdown={!currentAccountDetails?.samlPartnerId}
            />
        );
    }

    private _addUser(): void {
        this.setState({
            currentUser: null,
            showAddEditDialog: true
        });
    }

    private _editUser(user: User): void {
        this.setState({
            currentUser: user,
            showAddEditDialog: true
        });
    }

    private _deleteUser(user: User): void {
        this.setState({
            currentUser: user,
            showConfirmDeleteDialog: true
        });
    }

    // MouseEventHandler<T>
    private _bulkAddUser(e: React.SyntheticEvent<HTMLAnchorElement>): void {
        if (e) {
            killEvent(e);
        }
        this.setState({
            showBulkAddDialog: true
        });
    }

    private _downloadUsers(e: React.SyntheticEvent<HTMLAnchorElement>): void {
        const { users, currentAccountDetails } = this.props;
        if (e) {
            killEvent(e);
        }
        let csv = 'Name, Email, Role(s), Invite Accepted?\n';
        users?.forEach(u => {
            csv += `${getName(u)},${u.userName},"${getRoleNames(u)}",${!u.isPending}\n`;
        });
        const hiddenElement = document.createElement('a');
        hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);
        hiddenElement.target = '_blank';
        hiddenElement.download = `${currentAccountDetails?.name} users.csv`;
        hiddenElement.click();
    }

    private _selectAccount(accountId: string): void {
        const { dispatch } = this.props;
        dispatch?.(selectAccount(accountId));
        this._clearSearch();
    }

    private _filterUsers(searchString: string): void {
        const { dispatch } = this.props;
        dispatch?.(filterUsers(searchString));
    }

    private _onProductSelect(productName: string): void {
        const { dispatch } = this.props;
        dispatch?.(selectProduct(productName));
        this._clearSearch();
    }

    private _clearSearch(): void {
        if (this._searchInput) {
            this._searchInput.clear();
        }
    }
}

export { App };
export default App;



// WEBPACK FOOTER //
// ./src/components/app.tsx